import React, { useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { TournamentProvider } from './contexts/TournamentContext';
import { AuthProvider } from './contexts/AuthContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { LogoProvider } from './contexts/LogoContext';
import { PlayerAuthProvider } from './contexts/PlayerAuthContext';
import PlayerNotificationBootstrap from './components/PlayerNotificationBootstrap';
import { ToastProvider } from './components/Toast';
import { TenantProvider } from './contexts/TenantContext';
import { LanguageProvider } from './contexts/LanguageContext';
import OrganizationLanguageSync from './components/OrganizationLanguageSync';
import AutoTranslator from './components/AutoTranslator';

import PWAIconUpdater from './components/PWAIconUpdater';
import pwaService from './services/pwaService';
import { clubSiteRedirectFor } from './lib/tenantPath';

// Layouts
import AppShell from './components/AppShell';
// import PlayerAppShell from './components/PlayerAppShell'; // Seems unused in imports list but referenced in comments

// Static component imports (guards, layouts, CSS) — must come before lazy() declarations
import AdminRoute from './components/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import './styles/print.css';
import './styles/pairing-system.css';
import './styles/branding.css';
import { clearChunkReloadFlag, lazyWithRetry } from './utils/lazyWithRetry';
import { ScrollToTop, RouteTransitions } from './components/motion/transitions';
import AppSplash from './components/AppSplash';
import TenantAccessGate from './components/TenantAccessGate';
import AppOpenRedirect from './components/AppOpenRedirect';
import AppDownloadPage from './components/AppDownloadPage';

// Pages — lazy-loaded so each route only downloads its chunk on first visit
const Dashboard                    = lazy(() => import('./pages/Dashboard'));
const TournamentList               = lazy(() => import('./pages/TournamentList'));
const TDTournamentSearch           = lazy(() => import('./pages/TDTournamentSearch'));
const TournamentScoring            = lazy(() => import('./pages/TournamentScoring'));
const TournamentDetail             = lazy(() => import('./pages/TournamentDetail'));
const ChatbotTournamentView        = lazy(() => import('./pages/ChatbotTournamentView'));
const TournamentManager            = lazy(() => import('./components/TournamentManager'));
const CreateTournamentWizard       = lazy(() => import('./pages/CreateTournamentWizard'));
const BrandedPublicTournamentDisplay = lazy(() => import('./pages/BrandedPublicTournamentDisplay'));
const FormPublicPage               = lazy(() => import('./pages/FormPublicPage'));
const FormAdminPage                = lazy(() => import('./pages/FormAdminPage'));
const FormsListPage                = lazy(() => import('./pages/FormsListPage'));
const FormCreatePage               = lazy(() => import('./pages/FormCreatePage'));
const PublicTournamentV2           = lazy(() => import('./pages/PublicTournamentV2'));
const PublicTournamentList         = lazy(() => import('./pages/PublicTournamentList'));
const PublicDiscoverPage           = lazy(() => import('./pages/PublicDiscoverPage'));
const PublicTournamentsEntry       = lazy(() => import('./pages/PublicDiscoverPage').then(m => ({ default: m.PublicTournamentsEntry })));
const PublicTournamentCodeEntry    = lazy(() => import('./pages/PublicTournamentCodeEntry'));
const PublicOrganizationTournament = lazy(() => import('./pages/PublicOrganizationTournament'));
const OrganizationList             = lazy(() => import('./pages/OrganizationList'));
const OrganizationSettings         = lazy(() => import('./pages/OrganizationSettings'));
const EmbedDemo                    = lazy(() => import('./pages/EmbedDemo'));
const Registration                 = lazy(() => import('./pages/Registration'));
const SectionPairingPage           = lazy(() => import('./pages/SectionPairingPage'));
const UserProfile                  = lazy(() => import('./pages/UserProfile'));
const LandingPage                  = lazy(() => import('./pages/LandingPage'));
const AdminManagementPage          = lazy(() => import('./pages/AdminManagementPage'));
const PricingPage                  = lazy(() => import('./pages/PricingPage'));
const SignInPage                   = lazy(() => import('./pages/SignInPage'));
const SignUpPage                   = lazy(() => import('./pages/SignUpPage'));
const TermsOfService               = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy                = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy                 = lazy(() => import('./pages/CookiePolicy'));
const HelpCenter                   = lazy(() => import('./pages/HelpCenter'));
const APIDocsPage                  = lazy(() => import('./pages/APIDocsPage'));

/** In-app navigation to /api/docs must hard-reload so Express serves the static directory HTML. */
function ApiDocsStaticRedirect() {
  useLayoutEffect(() => {
    window.location.replace('/api/docs');
  }, []);
  return (
    <div className="min-h-[50vh] flex items-center justify-center text-gray-500 text-sm">
      Opening API documentation…
    </div>
  );
}
const ChessGame                    = lazy(() => import('./pages/ChessGame'));
const PlayChess                    = lazy(() => import('./pages/PlayChess'));
const ObserveGame                  = lazy(() => import('./pages/ObserveGame'));
const GameView                     = lazy(() => import('./pages/GameView'));
const CreateGame                   = lazy(() => import('./pages/CreateGame'));
const DrawbackChess                = lazy(() => import('./pages/DrawbackChess'));
const DrawbackGlossary             = lazy(() => import('./pages/DrawbackGlossary'));
const DrawbackOTB                  = lazy(() => import('./pages/DrawbackOTB'));
const GameAnalysis                 = lazy(() => import('./pages/GameAnalysis'));
const GameViewer                   = lazy(() => import('./pages/GameViewer'));
const OpeningTreeAnalyzer          = lazy(() => import('./pages/OpeningTreeAnalyzer'));
const PlayerPerformance            = lazy(() => import('./components/PlayerPerformance'));
const PlayerDashboard              = lazy(() => import('./pages/PlayerDashboard'));
const PlayerProfile                = lazy(() => import('./pages/PlayerProfile'));
const PlayerLoginPage              = lazy(() => import('./pages/PlayerLoginPage'));
const ClubJoinPage                 = lazy(() => import('./pages/ClubJoinPage'));
const PlayerHomePage               = lazy(() => import('./pages/PlayerHomePage'));
const CoachHomePage                = lazy(() => import('./pages/CoachHomePage'));
const PlayerGamesPage              = lazy(() => import('./pages/PlayerGamesPage'));
const PlayerTournamentsPage        = lazy(() => import('./pages/PlayerTournamentsPage'));
const PlayerCalendarPage           = lazy(() => import('./pages/PlayerCalendarPage'));
const PlayerSettingsPage           = lazy(() => import('./pages/PlayerSettingsPage'));
const PlayerAuthCallback           = lazy(() => import('./pages/PlayerAuthCallback'));
const AuthCallback                 = lazy(() => import('./pages/AuthCallback'));
const LichessTokenForm             = lazy(() => import('./pages/LichessTokenForm'));
const ChesscomTokenForm            = lazy(() => import('./pages/ChesscomTokenForm'));
const LichessOAuthResult           = lazy(() => import('./pages/LichessOAuthResult'));
const ChesscomOAuthResult          = lazy(() => import('./pages/ChesscomOAuthResult'));
const WithdrawConfirmation         = lazy(() => import('./pages/WithdrawConfirmation'));
const ReentryConfirmation          = lazy(() => import('./pages/ReentryConfirmation'));
const SecondaryCameraPage          = lazy(() => import('./pages/SecondaryCameraPage'));
const TDSpectate                   = lazy(() => import('./pages/TDSpectate'));
const MobileTDDashboard            = lazy(() => import('./pages/MobileTDDashboard'));
const TournamentGameDatabase       = lazy(() => import('./pages/TournamentGameDatabase'));
const GlobalGameDatabase           = lazy(() => import('./components/GlobalGameDatabase'));
const TenantWebsitePage            = lazy(() => import('./pages/TenantWebsitePage'));
const TenantGrandPrixDetailPage    = lazy(() => import('./pages/TenantGrandPrixDetailPage'));
const TenantTeamLeagueDetailPage   = lazy(() => import('./pages/TenantTeamLeagueDetailPage'));
const PublicTeamLeaguePage         = lazy(() => import('./pages/PublicTeamLeaguePage'));
const LeaguesListPage              = lazy(() => import('./pages/LeaguesListPage'));
const OrgShadowRoutes              = lazy(() => import('./pages/OrgShadowRoutes'));
const AppTournamentView            = lazy(() => import('./pages/AppTournamentView'));
const SnapshotViewer               = lazy(() => import('./pages/SnapshotViewer'));
const NotFoundPage                 = lazy(() => import('./pages/NotFoundPage'));
const NetworkErrorPage             = lazy(() => import('./pages/NetworkErrorPage'));
const BroadcastLinksPage           = lazy(() => import('./pages/BroadcastLinksPage'));
const BroadcastLayout              = lazy(() => import('./pages/broadcast/BroadcastLayout'));
const LowerThird                   = lazy(() => import('./pages/broadcast/LowerThird'));
const TickerTape                   = lazy(() => import('./pages/broadcast/TickerTape'));
const BroadcastStandings           = lazy(() => import('./pages/broadcast/BroadcastStandings'));
const LiveGames                    = lazy(() => import('./pages/broadcast/LiveGames'));
const BroadcastIndex               = lazy(() => import('./pages/broadcast/BroadcastIndex'));
const CountdownTimer               = lazy(() => import('./pages/broadcast/CountdownTimer'));
const FeaturedPlayer               = lazy(() => import('./pages/broadcast/FeaturedPlayer'));
const HeadToHead                   = lazy(() => import('./pages/broadcast/HeadToHead'));
const ChessClock                   = lazy(() => import('./pages/broadcast/ChessClock'));
const TournamentBracket            = lazy(() => import('./pages/broadcast/TournamentBracket'));
const CommentatorPanel             = lazy(() => import('./pages/broadcast/CommentatorPanel'));
const MoveList                     = lazy(() => import('./pages/broadcast/MoveList'));
const SponsorTicker                = lazy(() => import('./pages/broadcast/SponsorTicker'));
const AlertOverlay                 = lazy(() => import('./pages/broadcast/AlertOverlay'));
const MultiGameView                = lazy(() => import('./pages/broadcast/MultiGameView'));
const BroadcastStreamView          = lazy(() => import('./pages/broadcast/BroadcastStreamView'));
const BroadcastStreamLive          = lazy(() => import('./pages/broadcast/BroadcastStreamLive'));
const DGTBroadcastLive             = lazy(() => import('./pages/broadcast/DGTBroadcastLive'));
const BroadcastRecentGamesView     = lazy(() => import('./pages/broadcast/BroadcastRecentGamesView'));
const GameResultsFeed              = lazy(() => import('./pages/GameResultsFeed'));
const TrainingBoard                = lazy(() => import('./pages/TrainingBoard'));
const AnalysisBoardPage            = lazyWithRetry(() => import('./pages/AnalysisBoardPage'));
const CreateBoardPage              = lazyWithRetry(() => import('./pages/CreateBoardPage'));
const TrainingSessionsManager      = lazy(() => import('./pages/TrainingSessionsManager'));
const MyClubSpace                  = lazyWithRetry(() => import('./pages/MyClubSpace'));
const TacticSolver                 = lazy(() => import('./pages/TacticSolver'));
const HomeworkBoardPage            = lazyWithRetry(() => import('./pages/HomeworkBoardPage'));
const PuzzleAssignmentSolver       = lazy(() => import('./pages/PuzzleAssignmentSolver'));
const ShadowBot                    = lazy(() => import('./pages/ShadowBot'));

// /api/docs is served as a static HTML page by Express.
// This component triggers a hard reload so the browser fetches the static file
// instead of letting React Router intercept it on client-side navigation.
const ApiDocsReload: React.FC = () => {
  React.useEffect(() => {
    window.location.replace('/api/docs');
  }, []);
  return null;
};

// Wrapper for fullscreen play chess
const PlayChessWrapper: React.FC = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const isFullscreen = urlParams.get('fullscreen') === 'true' || urlParams.has('room');

  if (isFullscreen) {
    return (
      <ErrorBoundary>
        <PlayChess />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <PlayChess />
    </ErrorBoundary>
  );
};

// Wrapper for fullscreen observe game
const ObserveGameWrapper: React.FC = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const isFullscreen = urlParams.get('fullscreen') === 'true' || urlParams.has('room');

  if (isFullscreen) {
    return (
      <ErrorBoundary>
        <ObserveGame />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ObserveGame />
    </ErrorBoundary>
  );
};

// Redirect helper for legacy broadcast routes
const RedirectToStream: React.FC = () => {
  const { tournamentId } = useParams();
  if (!tournamentId) return <Navigate to="/broadcast" replace />;
  return <Navigate to={`/broadcast/${tournamentId}/view/stream`} replace />;
};

// Redirect helper for legacy player dashboard routes
const PlayerDashboardRedirect: React.FC = () => {
  const { tournamentId, playerId } = useParams();
  const location = useLocation();
  const isPublic = location.pathname.includes('/public/');

  if (isPublic) {
    return <Navigate to={`/public/tournaments/${tournamentId}/player/${playerId}`} replace />;
  }
  return <Navigate to={`/tournaments/${tournamentId}/player/${playerId}`} replace />;
};

/**
 * A path that moved on a host with its own club site (uchicago's tournament
 * list → its events page). The server redirects a direct hit; this catches
 * in-app navigation, which never leaves the browser.
 */
const ClubSiteRedirect: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation();
  const target = clubSiteRedirectFor(window.location.hostname, location.pathname);
  if (!target) return children;
  return <Navigate to={`${target}${location.search}`} replace />;
};

/** Redirect /public/organizations/:slug → /org/:slug shadow tenant site */
const PublicOrgToShadowRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/org/${slug}`} replace />;
};

// Protected route with AppShell layout for authenticated users
const AppShellRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
};

function App() {
  useEffect(() => {
    clearChunkReloadFlag();
    // Initialize PWA service
    pwaService.init();
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
      <ToastProvider>
        <FeaturesProvider>
        <AuthProvider>
          <PlayerAuthProvider>
            <PlayerNotificationBootstrap />
            <TournamentProvider>
              <Router>
                <TenantProvider>
                <LogoProvider>
                  <PWAIconUpdater />
                  <AutoTranslator />
                  <ScrollToTop />
                    <div className="min-h-screen bg-gray-50">
                      <Suspense fallback={<AppSplash />}>
                      <RouteTransitions>
                      <TenantAccessGate>
                      <Routes>
                        {/* Player app entry (PWA start_url) */}
                        <Route path="/app" element={<AppOpenRedirect />} />

                        {/* Native app download / install page */}
                        <Route path="/downapp" element={<AppDownloadPage continueTo="/app" />} />

                        {/* Public Landing Pages */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        {/* Legacy public landing pages → unified discover */}
                        <Route path="/public" element={<Navigate to="/discover" replace />} />
                        <Route path="/public/beta" element={<Navigate to="/discover" replace />} />
                        <Route path="/sign-in" element={<SignInPage />} />
                        <Route path="/sign-up" element={<SignUpPage />} />

                        {/* Support & Help */}
                        <Route path="/help" element={<HelpCenter />} />
                        <Route path="/support" element={<HelpCenter />} />

                        {/* Broadcast Landing Views (Premium, Standalone) */}
                        {/* Main Stream Builder Dashboard (Target) */}
                        <Route path="/broadcast/:tournamentId/view/stream" element={<BroadcastStreamView />} />
                        {/* Live Stream Output (Generated Link - Preserved) */}
                        <Route path="/broadcast/:tournamentId/stream/live" element={<BroadcastStreamLive />} />
                        <Route path="/broadcast/:tournamentId/stream/dgt" element={<DGTBroadcastLive />} />

                        {/* Redirect all other broadcast view routes to Stream Builder */}
                        <Route path="/broadcast/:tournamentId/view/*" element={<RedirectToStream />} />
                        <Route path="/broadcast/:tournamentId" element={<RedirectToStream />} />

                        {/* Game Results Feed */}
                        <Route path="/game-results" element={<GameResultsFeed />} />
                        <Route path="/game-results/:tournamentId" element={<GameResultsFeed />} />
                        <Route path="/broadcast/:tournamentId/view/recent" element={<BroadcastRecentGamesView />} />

                        {/* Broadcast Overlays (OBS - Transparent) */}
                        <Route path="/broadcast" element={<BroadcastLayout />}>
                          <Route index element={<BroadcastIndex />} />
                          <Route path=":tournamentId/lower-third" element={<LowerThird />} />
                          <Route path=":tournamentId/ticker" element={<TickerTape />} />
                          <Route path=":tournamentId/standings" element={<BroadcastStandings />} />
                          <Route path=":tournamentId/live-games" element={<LiveGames />} />
                          <Route path=":tournamentId/countdown" element={<CountdownTimer />} />
                          <Route path=":tournamentId/featured-player" element={<FeaturedPlayer />} />
                          <Route path=":tournamentId/head-to-head" element={<HeadToHead />} />
                          <Route path=":tournamentId/clock" element={<ChessClock />} />
                          <Route path=":tournamentId/bracket" element={<TournamentBracket />} />
                          <Route path=":tournamentId/commentators" element={<CommentatorPanel />} />
                          <Route path=":tournamentId/moves" element={<MoveList />} />
                          <Route path=":tournamentId/sponsors" element={<SponsorTicker />} />
                          <Route path=":tournamentId/alerts" element={<AlertOverlay />} />
                          <Route path=":tournamentId/multi-game" element={<MultiGameView />} />
                        </Route>

                        {/* API Documentation
                            /api/docs       → static Kalshi-style directory (served by Express)
                            /api/docs/reference → React APIDocsPage (interactive v1 reference) */}
                        <Route path="/api/docs" element={<ApiDocsReload />} />
                        <Route path="/api/docs/reference" element={<APIDocsPage />} />

                        {/* Auth Callback */}
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/player/auth/callback" element={<PlayerAuthCallback />} />

                        {/* Legal Pages */}
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/cookies" element={<CookiePolicy />} />

                        {/* Club self-registration (tenant sites) */}
                        <Route path="/org/join" element={<ClubJoinPage />} />
                        <Route path="/club/join" element={<Navigate to="/org/join" replace />} />

                        {/* Player Auth Routes */}
                        <Route path="/player/login" element={<PlayerLoginPage />} />
                        <Route path="/player/home" element={<PlayerHomePage />} />
                        <Route path="/coach/home" element={<CoachHomePage />} />
                        <Route path="/player/games" element={<PlayerGamesPage />} />
                        <Route path="/player/tournaments" element={<PlayerTournamentsPage />} />
                        <Route path="/player/calendar" element={<PlayerCalendarPage />} />
                        <Route path="/player/settings" element={<PlayerSettingsPage />} />
                        <Route path="/player/club/:organizationId" element={<MyClubSpace />} />
                        <Route path="/player/:uscfId" element={<PlayerProfile />} />

                        {/* Discover — canonical player tournament discovery */}
                        <Route path="/discover" element={<PublicDiscoverPage />} />

                        {/* Opening Tree Analyzer - Chess.com game analysis */}
                        <Route path="/opening-tree" element={<OpeningTreeAnalyzer />} />

                        {/* App-integrated tournament view with PlayerAppShell */}
                        <Route path="/app/tournament/:id" element={<AppTournamentView />} />

                        {/* App Routes - Wrapped in AppShell */}
                        <Route path="/*" element={
                          <OrganizationProvider>
                            <Routes>
                              {/* Dashboard - Main authenticated landing */}
                              <Route path="/dashboard" element={
                                <AppShellRoute>
                                  <Dashboard />
                                </AppShellRoute>
                              } />

                              {/* Team Leagues — dedicated area (a league is a team tournament; manage in TournamentDetail) */}
                              <Route path="/leagues" element={
                                <AppShellRoute>
                                  <LeaguesListPage />
                                </AppShellRoute>
                              } />

                              {/* Settings */}
                              <Route path="/settings" element={
                                <AppShellRoute>
                                  <UserProfile />
                                </AppShellRoute>
                              } />

                              {/* Profile - Redirect to settings */}
                              <Route path="/profile" element={
                                <Navigate to="/settings" replace />
                              } />

                              {/* Admin Management */}
                              <Route path="/manage" element={
                                <AdminRoute>
                                  <AppShellRoute>
                                    <AdminManagementPage />
                                  </AppShellRoute>
                                </AdminRoute>
                              } />

                              {/* Tournament Management Routes */}
                              <Route path="/tournaments" element={
                                <AppShellRoute>
                                  <TournamentList />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/new" element={
                                <AppShellRoute>
                                  <CreateTournamentWizard />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/scoring" element={
                                <AppShellRoute>
                                  <TDTournamentSearch />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/:id/scoring" element={
                                <ProtectedRoute>
                                  <TournamentScoring />
                                </ProtectedRoute>
                              } />
                              <Route path="/tournaments/:id" element={
                                <AppShellRoute>
                                  <TournamentDetail />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/:id/chatbot" element={
                                <AppShellRoute>
                                  <ChatbotTournamentView />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/:tournamentId/pairings/:sectionName" element={
                                <AppShellRoute>
                                  <SectionPairingPage />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/:tournamentId/game-database" element={
                                <AppShellRoute>
                                  <TournamentGameDatabase />
                                </AppShellRoute>
                              } />
                              <Route path="/tournaments/:tournamentId/director" element={
                                <ProtectedRoute>
                                  <AppShell>
                                    <TournamentManager tournamentId={window.location.pathname.split('/')[2]} />
                                  </AppShell>
                                </ProtectedRoute>
                              } />

                              {/* Game Database */}
                              <Route path="/game-database" element={
                                <AppShellRoute>
                                  <GlobalGameDatabase />
                                </AppShellRoute>
                              } />

                              {/* Mobile TD Dashboard */}
                              <Route path="/mobile/td/:tournamentId" element={
                                <ProtectedRoute>
                                  <MobileTDDashboard />
                                </ProtectedRoute>
                              } />

                              {/* Organization Routes */}
                              <Route path="/organizations" element={
                                <AppShellRoute>
                                  <OrganizationList />
                                </AppShellRoute>
                              } />
                              <Route path="/organizations/:id/settings" element={
                                <ProtectedRoute>
                                  <AppShell>
                                    <OrganizationSettings />
                                  </AppShell>
                                </ProtectedRoute>
                              } />

                              {/* Tournament Snapshot Viewer — requires login */}
                              <Route path="/snapshot" element={
                                <ProtectedRoute>
                                  <SnapshotViewer />
                                </ProtectedRoute>
                              } />

                              {/* Game Analysis */}
                              <Route path="/game-analysis" element={
                                <AppShellRoute>
                                  <GameAnalysis />
                                </AppShellRoute>
                              } />

                              {/* Collaborative analysis boards (training + standalone) */}
                              <Route path="/boards/new" element={<CreateBoardPage />} />
                              <Route path="/analysis" element={<AnalysisBoardPage />} />
                              <Route path="/analysis/:boardCode" element={<AnalysisBoardPage />} />
                              <Route path="/boards/:boardCode" element={<AnalysisBoardPage />} />
                              <Route path="/training/:roomCode" element={<TrainingBoard />} />
                              <Route path="/tactics/:id" element={<TacticSolver />} />
                              <Route path="/homework-board/:homeworkId" element={<HomeworkBoardPage />} />
                              <Route path="/puzzle-assignment/:assignmentId" element={<PuzzleAssignmentSolver />} />
                              <Route path="/shadow-bot" element={<ShadowBot />} />
                              <Route path="/training-sessions/:organizationId" element={
                                <AppShellRoute>
                                  <TrainingSessionsManager />
                                </AppShellRoute>
                              } />
                              <Route path="/my-club/:organizationId" element={
                                <AppShellRoute>
                                  <MyClubSpace />
                                </AppShellRoute>
                              } />

                              {/* Chess/Game Routes */}
                              <Route path="/chess" element={<ChessGame />} />
                              <Route path="/play-chess" element={<PlayChessWrapper />} />
                              <Route path="/observe-game" element={<ObserveGameWrapper />} />
                              <Route path="/create-game" element={<CreateGame />} />
                              <Route path="/drawback-chess" element={<ErrorBoundary><DrawbackChess /></ErrorBoundary>} />
                              <Route path="/drawback-chess/glossary" element={<DrawbackGlossary />} />
                              <Route path="/drawback-chess/otb" element={<DrawbackOTB />} />
                              <Route path="/chess960" element={<ErrorBoundary><DrawbackChess /></ErrorBoundary>} />
                              <Route path="/chess-roulette" element={<ErrorBoundary><DrawbackChess /></ErrorBoundary>} />
                              {/* One catalogue page, reachable from either game. */}
                              <Route path="/chess-roulette/glossary" element={<DrawbackGlossary />} />
                              <Route path="/game/:gameId/:color/:token" element={<GameView />} />
                              <Route path="/game/view/:roomCode" element={<GameViewer />} />

                              {/* Public Tournament Routes */}
                              <Route path="/public/open" element={<PublicTournamentCodeEntry />} />
                              <Route path="/public/tournaments" element={
                                <ClubSiteRedirect>
                                  <PublicTournamentsEntry tenantList={<PublicTournamentList />} />
                                </ClubSiteRedirect>
                              } />
                              <Route path="/public/tournaments/:id" element={<BrandedPublicTournamentDisplay />} />
                              {/* A club form is its own page, not a tournament with the tabs switched off. */}
                              <Route path="/forms/:formId" element={<FormPublicPage />} />
                              {/* Managing a form is app work, so it keeps the app's chrome. */}
                              <Route path="/manage/forms" element={<AppShellRoute><FormsListPage /></AppShellRoute>} />
                              {/* Before the :formId route: a static segment must not be read as an id. */}
                              <Route path="/manage/forms/new" element={<AppShellRoute><FormCreatePage /></AppShellRoute>} />
                              <Route path="/manage/forms/:formId" element={<AppShellRoute><FormAdminPage /></AppShellRoute>} />
                              <Route path="/public/tournaments/:id/v2" element={<PublicTournamentV2 />} />
                              <Route path="/public/tournament/:id" element={<BrandedPublicTournamentDisplay />} />

                              {/* Public team-league pages (grand-prix style season of team matchdays) */}
                              <Route path="/public/leagues/:orgSlug/:leagueSlug" element={<PublicTeamLeaguePage />} />

                              {/* Player Performance & Dashboard Routes */}
                              <Route path="/public/tournaments/:tournamentId/player/:playerId" element={<PlayerPerformance />} />
                              <Route path="/tournaments/:tournamentId/player/:playerId" element={<PlayerPerformance />} />
                              <Route path="/player/:tournamentId/:playerId/dashboard" element={<PlayerDashboardRedirect />} />
                              <Route path="/public/tournaments/:tournamentId/player/:playerId/dashboard" element={<PlayerDashboardRedirect />} />

                              {/* Lichess player token submission (public, no auth) */}
                              <Route path="/lichess-token/:playerId" element={<LichessTokenForm />} />
                              <Route path="/chesscom-auth/:playerId" element={<ChesscomTokenForm />} />
                              <Route path="/lichess-oauth-result" element={<LichessOAuthResult />} />
                              <Route path="/chesscom-oauth-result" element={<ChesscomOAuthResult />} />
                              <Route path="/chesscom-oauth-result/*" element={<ChesscomOAuthResult />} />

                              {/* Tournament Actions */}
                              <Route path="/tournaments/:id/withdraw/:token" element={<WithdrawConfirmation />} />
                              <Route path="/tournaments/:id/reentry/:token" element={<ReentryConfirmation />} />
                              <Route path="/register/:tournamentId" element={<Registration />} />
                              <Route path="/registration/:tournamentId" element={<Registration />} />

                              {/* Organization Public Routes — shadow tenant sites at /org/:slug/* */}
                              <Route path="/public/organizations" element={<Navigate to="/discover" replace />} />
                              <Route path="/public/organizations/:slug" element={<PublicOrgToShadowRedirect />} />
                              <Route path="/public/organizations/:slug/tournaments/:tournamentId" element={<PublicOrganizationTournament />} />
                              <Route path="/org/:slug/*" element={<OrgShadowRoutes />} />
                              {/* A club whose domain root is its own static site keeps the
                                  platform here: /events is the hub, /events/<id> an event. */}
                              <Route path="/events/*" element={<OrgShadowRoutes />} />
                              <Route path="/site/grand-prix/:seriesSlug" element={<TenantGrandPrixDetailPage />} />
                              <Route path="/site/league/:leagueSlug" element={<TenantTeamLeagueDetailPage />} />
                              <Route path="/site/:pageSlug" element={<TenantWebsitePage />} />

                              {/* Proctoring */}
                              <Route path="/secondary-camera" element={<SecondaryCameraPage />} />
                              <Route path="/td-spectate" element={<TDSpectate />} />

                              {/* Embed Demo */}
                              <Route path="/embed-demo" element={<EmbedDemo />} />

                              {/* Broadcast Kit */}
                              <Route path="/tournaments/:id/broadcast" element={
                                <AppShellRoute>
                                  <BroadcastLinksPage />
                                </AppShellRoute>
                              } />

                              {/* Network Error Page */}
                              <Route path="/network-error" element={<NetworkErrorPage />} />

                              {/* 404 */}
                              <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                          </OrganizationProvider>
                        } />
                      </Routes>
                      </TenantAccessGate>
                      </RouteTransitions>
                      </Suspense>
                      <CookieConsent />
                    </div>
                  </LogoProvider>
                </TenantProvider>
                </Router>
              </TournamentProvider>
            </PlayerAuthProvider>
          </AuthProvider>
        </FeaturesProvider>
        </ToastProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
